import { type ComponentProps, memo, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { type Board } from '@air/api/types';

import { BoardSelectBoardListItem } from '~/components/BoardSelect/BoardSelectBoardListItem';
import { BoardSelectLibraryListItem } from '~/components/BoardSelect/BoardSelectLibraryListItem';
import { BoardSelectList } from '~/components/BoardSelect/components/BoardSelectList';
import { BoardSelectListHeader } from '~/components/BoardSelect/components/BoardSelectListHeader';
import { BoardSelectListItem } from '~/components/BoardSelect/components/BoardSelectListItem';
import { BoardSelectSearch, type BoardSelectSearchProps } from '~/components/BoardSelect/components/BoardSelectSearch';
import {
  getBoardSelectInitialIndex,
  getBoardSelectInitiallyExpandedBoardIds,
} from '~/components/BoardSelect/getBoardSelectInitialIndex';
import { useGeneralLibraryTitle } from '~/components/LibraryBeta/hooks/useGeneralLibraryTitle';
import { GeneralLibraryIcon } from '~/components/LibraryBeta/LibraryNavigation/components/LibraryNavigationGeneralItem/GeneralLibraryIcon';
import { GeneralLibrary } from '~/utils/librariesUtils';

export type BoardSelectLibrary = Required<Board>['library'];

/**
 * A nested board can mount while TreeList still sizes its parent from the
 * collapsed estimate, so the first alignment clamps. Measurement often lands
 * on a later frame. About two seconds of frames covers a busy first paint.
 * A later resize starts another pass, and the pass stops for good once the
 * user scrolls the list.
 */
const INITIAL_BOARD_ALIGNMENT_FRAME_LIMIT = 120;

export type BoardSelectProps = Pick<ComponentProps<'div'>, 'children'> & {
  boards: Board[];
  getBoardDisabledMessage?: (board: Board) => string | undefined;
  getLibraryDisabledMessage?: (library: BoardSelectLibrary) => string | undefined;
  hasLibraries?: boolean;
  initialBoard?: Pick<Board, 'ancestors' | 'id' | 'library' | 'title'>;
  isBoardDisabled?: (board: Board) => boolean;
  isBoardSelected?: (board: Board) => boolean;
  isLibraryDisabled?: (library: BoardSelectLibrary) => boolean;
  isLibrarySelected?: (library: BoardSelectLibrary) => boolean;
  libraries: BoardSelectLibrary[];
  onCreateBoard?: () => void;
  onCreateLibrary?: () => void;
  onSelectBoard?: (board: Pick<Board, 'id' | 'title' | 'ancestors' | 'thumbnails' | 'library'> | null) => void;
  onSelectLibrary?: (library: BoardSelectLibrary | undefined) => void;
  onSearchChange?: BoardSelectSearchProps['onChange'];
  search?: string;
  allowCreateSubBoard?: boolean;
  shouldShowSubBoards?: (id: Board['id']) => boolean;
  topContent?: ReactNode;
};

export const BoardSelect = memo(
  ({
    boards,
    children,
    getBoardDisabledMessage,
    getLibraryDisabledMessage,
    hasLibraries,
    initialBoard,
    isBoardDisabled,
    isBoardSelected,
    isLibrarySelected,
    libraries,
    search,
    onCreateBoard,
    onCreateLibrary,
    onSearchChange,
    onSelectBoard,
    onSelectLibrary,
    allowCreateSubBoard,
    shouldShowSubBoards,
    topContent,
  }: BoardSelectProps) => {
    /**
     * We use `useState` instead of `ref` to ensure the scroll element is always up to date.
     */
    const { generalLibraryTitle } = useGeneralLibraryTitle();
    const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>();
    const [initialBoardRow, setInitialBoardRow] = useState<HTMLDivElement | null>(null);
    const [hasAlignedInitialBoard, setHasAlignedInitialBoard] = useState(false);
    const hasScrolledToInitialBoard = useRef<boolean>(false);
    const [expanded, setExpanded] = useState<{
      libraries: Set<BoardSelectLibrary['id']>;
      boards: Set<Board['id']>;
    }>({
      libraries: initialBoard?.library ? new Set([initialBoard.library.id]) : new Set([GeneralLibrary.id]),
      boards: getBoardSelectInitiallyExpandedBoardIds(initialBoard),
    });

    /**
     * TreeList scrolls the root row to the top from a collapsed size estimate,
     * and this effect runs after that because BoardSelect is the parent. A
     * nested board often registers before the estimate grows, so `scrollTop`
     * clamps short of the row. Measuring that row then corrects scroll, which
     * can land after the row first looks settled and push it back out. Keep
     * aligning through those corrections, then stop. A scrollbar drag does not
     * emit `wheel`, so a pointer on the list stops early too. Later remounts
     * must not pull the board back into view.
     * This scrolls only the scroll box on purpose: `element.scrollIntoView`
     * would also scroll the modal body. The registered element is the board's
     * own row, not the wrapper that also holds its expanded sub-boards.
     */
    useEffect(() => {
      if (!initialBoardRow || !scrollElement || hasScrolledToInitialBoard.current) {
        return;
      }

      let frameId = 0;
      let frames = 0;
      let framesInside = 0;
      let stopped = false;
      let publishedAlignment = false;
      let lastScrollTop = scrollElement.scrollTop;

      const stopAligning = () => {
        if (stopped) {
          return;
        }

        stopped = true;
        hasScrolledToInitialBoard.current = true;
        cancelAnimationFrame(frameId);
        frameId = 0;
        resizeObserver.disconnect();
        scrollElement.removeEventListener('wheel', stopAligning);
        scrollElement.removeEventListener('touchstart', stopAligning);
        scrollElement.removeEventListener('pointerdown', stopAligning);
        scrollElement.removeEventListener('keydown', stopAligning);

        if (!publishedAlignment) {
          publishedAlignment = true;
          setHasAlignedInitialBoard(true);
        }
      };

      const alignInitialBoardRow = () => {
        if (stopped || !initialBoardRow.isConnected) {
          return false;
        }

        const rowRect = initialBoardRow.getBoundingClientRect();
        const boxRect = scrollElement.getBoundingClientRect();

        // An unlaid-out row reports an empty box that sits inside the scroll box.
        // Ignoring it avoids treating the modal's first paint as finished.
        if (rowRect.height === 0 || boxRect.height === 0) {
          return false;
        }

        if (rowRect.top < boxRect.top) {
          scrollElement.scrollTop += rowRect.top - boxRect.top;
        } else if (rowRect.bottom > boxRect.bottom) {
          scrollElement.scrollTop += rowRect.bottom - boxRect.bottom;
        }

        const alignedRowRect = initialBoardRow.getBoundingClientRect();
        const alignedBoxRect = scrollElement.getBoundingClientRect();

        return alignedRowRect.top >= alignedBoxRect.top - 1 && alignedRowRect.bottom <= alignedBoxRect.bottom + 1;
      };

      const scheduleAlignmentFrame = () => {
        if (stopped || frameId !== 0) {
          return;
        }

        frameId = requestAnimationFrame(alignUntilRowIsVisible);
      };

      const alignUntilRowIsVisible = () => {
        frameId = 0;

        const isRowInsideScrollBox = alignInitialBoardRow();

        if (stopped || !initialBoardRow.isConnected) {
          return;
        }

        const scrollTop = scrollElement.scrollTop;
        const scrollMoved = Math.abs(scrollTop - lastScrollTop) > 1;
        lastScrollTop = scrollTop;

        if (!isRowInsideScrollBox || scrollMoved) {
          framesInside = 0;
        } else {
          framesInside += 1;

          if (framesInside >= 2) {
            stopAligning();
            return;
          }
        }

        frames += 1;

        if (frames >= INITIAL_BOARD_ALIGNMENT_FRAME_LIMIT) {
          return;
        }

        scheduleAlignmentFrame();
      };

      const resizeObserver = new ResizeObserver(() => {
        if (stopped) {
          return;
        }

        framesInside = 0;

        if (frameId === 0) {
          frames = 0;
          scheduleAlignmentFrame();
        }
      });

      scrollElement.addEventListener('wheel', stopAligning, { passive: true });
      scrollElement.addEventListener('touchstart', stopAligning, { passive: true });
      scrollElement.addEventListener('pointerdown', stopAligning);
      scrollElement.addEventListener('keydown', stopAligning);

      let ancestor = initialBoardRow.parentElement;

      while (ancestor && ancestor !== scrollElement) {
        resizeObserver.observe(ancestor);
        ancestor = ancestor.parentElement;
      }

      scheduleAlignmentFrame();

      return () => {
        stopped = true;
        cancelAnimationFrame(frameId);
        resizeObserver.disconnect();
        scrollElement.removeEventListener('wheel', stopAligning);
        scrollElement.removeEventListener('touchstart', stopAligning);
        scrollElement.removeEventListener('pointerdown', stopAligning);
        scrollElement.removeEventListener('keydown', stopAligning);
      };
    }, [initialBoardRow, scrollElement]);

    const isBoardExpanded = useCallback((id: string) => expanded.boards.has(id), [expanded.boards]);

    const isLibraryExpanded = useCallback(
      (library: Pick<BoardSelectLibrary, 'id'>) => expanded.libraries.has(library.id),
      [expanded.libraries],
    );

    const onBoardOpenChange = useCallback(({ id, isOpen }: { id: Board['id']; isOpen?: boolean }) => {
      setExpanded((prev) => {
        const { boards } = prev;
        const newBoards = new Set(boards);

        if (isOpen) {
          newBoards.add(id);
        } else {
          newBoards.delete(id);
        }

        return {
          ...prev,
          boards: newBoards,
        };
      });
    }, []);

    const onLibraryOpenChange = useCallback(({ id, isOpen }: { id: BoardSelectLibrary['id']; isOpen?: boolean }) => {
      setExpanded((prev) => {
        const { libraries } = prev;
        const newLibraries = new Set(libraries);

        if (isOpen) {
          newLibraries.add(id);
        } else {
          newLibraries.delete(id);
        }

        return {
          ...prev,
          libraries: newLibraries,
        };
      });
    }, []);

    const boardsListEl = scrollElement ? (
      <BoardSelectList<Board>
        initialIndex={!search ? getBoardSelectInitialIndex(boards, initialBoard) : undefined}
        items={boards}
        onCreate={onCreateBoard}
        // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing inline handler, flagged by changed-file lint
        renderListItem={({ data }) => (
          <BoardSelectBoardListItem
            allowCreateSubBoard={allowCreateSubBoard}
            board={data}
            getBoardDisabledMessage={getBoardDisabledMessage}
            initialBoardId={search ? undefined : initialBoard?.id}
            isBoardDisabled={isBoardDisabled}
            onInitialBoardRow={setInitialBoardRow}
            isBoardExpanded={isBoardExpanded}
            isBoardSelected={isBoardSelected}
            onSelectBoard={onSelectBoard}
            onBoardOpenChange={onBoardOpenChange}
            shouldShowSubBoards={shouldShowSubBoards}
          />
        )}
        scrollElement={scrollElement}
        title="Boards"
      />
    ) : null;

    return (
      <div className="flex w-full flex-col gap-3">
        {!!onSearchChange && (
          <BoardSelectSearch
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing inline handler, flagged by changed-file lint
            onChange={(event) => {
              onSearchChange(event);
              /**
               * Scrolls to the top of the list when the search changes.
               */
              scrollElement?.scrollTo(0, 0);
            }}
          />
        )}

        <div
          className="flex h-[320px] flex-col overflow-y-auto rounded-4 border ads:border-ads-gray-5 border-gray-5 ads:bg-base bg-gray-1 p-2 pt-4"
          data-initial-board-aligned={hasAlignedInitialBoard ? 'true' : 'false'}
          data-testid="BOARD_SELECT_SCROLL_CONTAINER"
          // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing inline handler, flagged by changed-file lint
          ref={(ref) => setScrollElement(ref)}
        >
          {topContent}
          <BoardSelectListHeader onCreate={onCreateBoard}>
            {hasLibraries ? 'Libraries' : 'Boards'}
          </BoardSelectListHeader>

          {!!scrollElement &&
            (!hasLibraries ? (
              boardsListEl
            ) : (
              <>
                {!!boards.length && (
                  <BoardSelectListItem
                    isExpanded={isLibraryExpanded({ id: GeneralLibrary.id })}
                    id="general-library"
                    // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing inline handler, flagged by changed-file lint
                    onSelect={() =>
                      onLibraryOpenChange({
                        id: GeneralLibrary.id,
                        isOpen: !isLibraryExpanded({ id: GeneralLibrary.id }),
                      })
                    }
                    // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing inline handler, flagged by changed-file lint
                    onOpenChange={(isOpen) => onLibraryOpenChange({ id: GeneralLibrary.id, isOpen })}
                    prefix={<GeneralLibraryIcon />}
                    title={generalLibraryTitle ?? 'General'}
                  >
                    {boardsListEl}
                  </BoardSelectListItem>
                )}
                {!!libraries.length && (
                  <BoardSelectList<BoardSelectLibrary>
                    initialIndex={
                      initialBoard?.library && !search
                        ? getBoardSelectInitialIndex(libraries, { id: initialBoard.library.id })
                        : undefined
                    }
                    items={libraries}
                    onCreate={onCreateLibrary}
                    // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing inline handler, flagged by changed-file lint
                    renderListItem={({ data }) => (
                      <BoardSelectLibraryListItem
                        getBoardDisabledMessage={getBoardDisabledMessage}
                        getLibraryDisabledMessage={getLibraryDisabledMessage}
                        initialBoardId={search ? undefined : initialBoard?.id}
                        isLibraryExpanded={isLibraryExpanded}
                        onInitialBoardRow={setInitialBoardRow}
                        library={data}
                        isBoardDisabled={isBoardDisabled}
                        isBoardExpanded={isBoardExpanded}
                        isBoardSelected={isBoardSelected}
                        isLibrarySelected={isLibrarySelected}
                        onBoardOpenChange={onBoardOpenChange}
                        onLibraryOpenChange={onLibraryOpenChange}
                        onSelectBoard={onSelectBoard}
                        onSelectLibrary={onSelectLibrary}
                        allowCreateSubBoard={allowCreateSubBoard}
                        search={search}
                        shouldShowSubBoards={shouldShowSubBoards}
                      />
                    )}
                    scrollElement={scrollElement}
                    title="Libraries"
                  />
                )}
              </>
            ))}

          {children}
        </div>
      </div>
    );
  },
);

BoardSelect.displayName = 'BoardSelect';
